import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Modal,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  HelpModalStyle,
  TutorialModalStyle,
} from "#next/components/modals/styles";

export const HelpModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [tutorialIsOpen, setTutorialIsOpen] = useState(false);
  const [tutorialIframeUrl, setTutorialIframeUrl] = useState<string | null>(
    null,
  );

  interface TutorialMap {
    name: string;
    url: string;
  }

  const tutorials: TutorialMap[] = [
    {
      name: "basic",
      url: "https://app.tango.us/app/embed/21345595-bca0-4ad9-ae4d-55dae4e59ea5?iframe=true",
    },
    {
      name: "complex",
      url: "https://app.tango.us/app/embed/8b3825df-55e2-494c-838e-cdced6189076?iframe=true",
    },
    {
      name: "complex-with-nested",
      url: "https://app.tango.us/app/embed/4c115a48-74a8-4100-93cb-ed62987979d8?iframe=true",
    },
    {
      name: "subscriptions",
      url: "https://app.tango.us/app/embed/dba39e73-ab75-44e4-8ba5-8f909e2ae968?iframe=true",
    },
  ];

  function handleClickOption(optionName: string) {
    const selectedIframeUrl =
      tutorials.filter((obj) => obj.name === optionName)[0]?.url || null;
    setTutorialIframeUrl(selectedIframeUrl);
    setTutorialIsOpen(true);
  }

  return (
    <Modal open={open} onClose={onClose}>
      {tutorialIsOpen && tutorialIframeUrl ? (
        <Box sx={TutorialModalStyle}>
          <iframe
            src={tutorialIframeUrl}
            sandbox="allow-scripts allow-top-navigation-by-user-activation allow-popups allow-same-origin"
            security="restricted"
            title="Create a basic project"
            width="100%"
            height="100%"
          ></iframe>
        </Box>
      ) : (
        <Box sx={HelpModalStyle}>
          <Typography
            sx={{ mt: 4, mb: 2, textAlign: "center" }}
            variant="h6"
            component="div"
          >
            Welcome to the Pyrra Help Center!
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <ListItemButton onClick={() => handleClickOption("basic")}>
                <ListItemAvatar sx={{ mt: 2 }}>
                  <Avatar
                    alt="basic project"
                    src="/images/create-project.png"
                    variant="rounded"
                    sx={{ width: 75, height: 75 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 3 }}
                  primary="Learn how to create a basic project"
                  secondary={
                    <Typography
                      sx={{ display: "inline", fontSize: 14 }}
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      Learn how to build simple queries, using relevant keywords
                      or phrases to review high level results. Drill down using
                      Pyrra&aposs built-in filters, and conduct research or
                      investigations from the top-down.
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem alignItems="flex-start">
              <ListItemButton onClick={() => handleClickOption("complex")}>
                <ListItemAvatar sx={{ mt: 2 }}>
                  <Avatar
                    alt="complex project"
                    src="/images/create-project.png"
                    variant="rounded"
                    sx={{ width: 75, height: 75 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 3 }}
                  primary="Learn how to create a complex project"
                  secondary={
                    <Typography
                      sx={{ display: "inline", fontSize: 14 }}
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      Learn how to build complex boolean queries with nested
                      groups that allows you to get hyper-focused results. Step
                      thru adding narratives to your search, and using those
                      narratives to filter your results even further.
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem alignItems="flex-start">
              <ListItemButton
                onClick={() => handleClickOption("complex-with-nested")}
              >
                <ListItemAvatar sx={{ mt: 2 }}>
                  <Avatar
                    alt="complex nested project"
                    src="/images/create-project.png"
                    variant="rounded"
                    sx={{ width: 75, height: 75 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 3 }}
                  primary="Learn how to create a complex project with other nested projects"
                  secondary={
                    <Typography
                      sx={{ display: "inline", fontSize: 14 }}
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      Learn how to build complex boolean queries with nested
                      projects that allows you to get hyper-focused results.
                      Step thru adding narratives to your search, and using
                      those narratives to filter your results even further.
                      Customize even further when you subscribe to alerts, and
                      receive email updates when your specific criteria is met.
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem alignItems="flex-start">
              <ListItemButton
                onClick={() => handleClickOption("subscriptions")}
              >
                <ListItemAvatar sx={{ mt: 2 }}>
                  <Avatar
                    alt="subscriptions"
                    src="/images/create-subscription.png"
                    variant="rounded"
                    sx={{ width: 75, height: 75 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  sx={{ ml: 3 }}
                  primary="Learn how to manage or create customized alert subscriptions"
                  secondary={
                    <Typography
                      sx={{ display: "inline", fontSize: 14 }}
                      component="span"
                      variant="body2"
                      color="text.primary"
                    >
                      Learn how to customize alert subscriptions, defining the
                      frequency of alerts, the type of language you&aposre
                      concerned with, and the recipients of the notifications.
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}
    </Modal>
  );
};
